// Client-side asset export helpers. Vectors stay as text; rasters are baked
// through <canvas>. No dependencies — the browser is the renderer.

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function svgBlob(svg: string): Blob {
  return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
}

export function downloadSvg(svg: string, filename: string) {
  downloadBlob(svgBlob(svg), filename);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Rasterize an SVG string to a PNG Blob at a given pixel size (square or w×h). */
export function svgToPng(svg: string, w: number, h = w): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no 2d context"));
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
    };
    img.onerror = () => reject(new Error("svg image load failed"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}

export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

/**
 * Pack one or more PNG blobs into a single .ico (PNG-encoded entries — accepted
 * by modern browsers and Windows). Sizes ≤ 256.
 */
export async function pngsToIco(entries: { size: number; blob: Blob }[]): Promise<Blob> {
  const imgs = await Promise.all(
    entries.map(async (e) => ({ size: e.size, data: new Uint8Array(await e.blob.arrayBuffer()) })),
  );
  const count = imgs.length;
  const header = new Uint8Array(6 + count * 16);
  const dv = new DataView(header.buffer);
  dv.setUint16(0, 0, true); // reserved
  dv.setUint16(2, 1, true); // type: icon
  dv.setUint16(4, count, true);

  let offset = header.length;
  const dirAndData: Uint8Array[] = [header];
  imgs.forEach((im, i) => {
    const b = 6 + i * 16;
    dv.setUint8(b + 0, im.size >= 256 ? 0 : im.size); // width  (0 = 256)
    dv.setUint8(b + 1, im.size >= 256 ? 0 : im.size); // height
    dv.setUint8(b + 2, 0); // palette
    dv.setUint8(b + 3, 0); // reserved
    dv.setUint16(b + 4, 1, true); // colour planes
    dv.setUint16(b + 6, 32, true); // bpp
    dv.setUint32(b + 8, im.data.length, true); // size of PNG
    dv.setUint32(b + 12, offset, true); // offset
    offset += im.data.length;
    dirAndData.push(im.data);
  });

  return new Blob(dirAndData as BlobPart[], { type: "image/x-icon" });
}

/** Build a clean, accent-aware filename: vova-mark--analytical--dark--512.png */
export function assetName(parts: (string | number | undefined)[], ext: string): string {
  return parts.filter((p) => p !== undefined && p !== "").join("--") + "." + ext;
}
